/**
 * Catches a specific Binder failure mode seen in three KOA-17026 traces:
 * the Binder picks the wrong source kuikaId for an action parameter when
 * the right type of input is missing. Concretely, the Binder queries
 * `find_by_type` for "NumberInput" / "DateInput" etc., gets back a list
 * the parameter shouldn't actually source from, and binds by ARRAY ORDER
 * — pairing `KayitTarihi` (DateTime) with the only TextArea on screen,
 * or `YagOrani` (Decimal) with whatever NumberInput is left over.
 *
 * Symptom on the canvas:
 *
 *   parameter: KayitTarihi  (action expects DateTime)
 *   sourceKuikaId: 551863   (TextArea, friendlyName "olcum-not")
 *
 * Result at runtime: the SQL action fires with the user's "Notlar (isteğe
 * bağlı)" text crammed into the KayitTarihi datetime column → either
 * parse error or a poison-row insert.
 *
 * The Binder's framing already says "use find_component_by_label, not
 * find_by_type, for action parameter source discovery" — but framing is
 * advisory. This validator is a runtime guard that the dispatcher cannot
 * route around: bindings whose parameter name shares ZERO meaningful
 * tokens with the source component's friendly name are rejected.
 *
 * Conservative posture by design — the guard only fires when there are
 * no shared tokens at all AND there is at least one alternative input on
 * the screen whose friendlyName DOES share a token. When no better
 * candidate exists the binding is allowed through with a warning, so a
 * legitimate Turkish-vs-English mismatch ("Email" → "eposta") doesn't
 * lock the LLM out of the only reasonable option.
 */

interface ScreenComponent {
  readonly kuikaId: string;
  readonly friendlyName: string;
  readonly componentName?: string;
}

export interface ParameterSourceValidationOptions {
  readonly parameterName: string;
  readonly sourceKuikaId: string;
  readonly sourceFriendlyName: string;
  readonly sourceComponentName?: string;
  /**
   * All other components on the same screen that the Binder might have
   * sourced from. Used to suggest better candidates when the chosen source
   * has no name affinity with the parameter. Pass an empty array to
   * suppress the candidate suggestion path (the warning still fires).
   */
  readonly otherComponents: readonly ScreenComponent[];
}

export interface ParameterSourceValidationResult {
  readonly error?: string;
  readonly warning?: string;
}

const MIN_TOKEN_LENGTH = 3;

/**
 * Tokenize an identifier the way a human would read it. Splits camelCase,
 * kebab/snake/dot delimiters, and lowercases. Tokens shorter than 3 chars
 * are dropped so noise like "id" / "at" / "of" doesn't carry the affinity
 * check (otherwise "CreatedAt" would falsely match "any-At-input").
 */
function tokenize(s: string): string[] {
  if (!s) return [];
  return s
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[-_./]+/g, " ")
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length >= MIN_TOKEN_LENGTH);
}

/**
 * True when `a` and `b` share at least one token, where "share" means
 * either an exact match or one token contains the other (so "tarih"
 * matches "tarihi", "kilo" matches "olcumkilo"). This is intentionally
 * permissive — false positives on the affinity check are far cheaper
 * than false rejections of legitimate bindings.
 */
function hasTokenOverlap(a: string, b: string): boolean {
  const ta = tokenize(a);
  const tb = tokenize(b);
  for (const x of ta) {
    for (const y of tb) {
      if (x === y || x.includes(y) || y.includes(x)) return true;
    }
  }
  return false;
}

export function validateParameterSourceAffinity(
  options: ParameterSourceValidationOptions
): ParameterSourceValidationResult {
  const { parameterName, sourceKuikaId, sourceFriendlyName, sourceComponentName, otherComponents } = options;

  // Cannot validate without both names.
  if (!parameterName || !sourceFriendlyName) return {};

  if (hasTokenOverlap(parameterName, sourceFriendlyName)) return {};

  const candidates = otherComponents.filter(
    (c) => c.kuikaId !== sourceKuikaId && hasTokenOverlap(parameterName, c.friendlyName)
  );

  const sourceLabel = sourceComponentName
    ? `'${sourceFriendlyName}' (kuikaId ${sourceKuikaId}, ${sourceComponentName})`
    : `'${sourceFriendlyName}' (kuikaId ${sourceKuikaId})`;

  if (candidates.length === 0) {
    return {
      warning:
        `Parameter '${parameterName}' bound to ${sourceLabel} — names share no tokens. Verify ` +
        `the source component is the intended input for this parameter (cross-language naming, e.g. ` +
        `'Email' parameter ↔ 'eposta' input, will trip this check).`
    };
  }

  const suggestionText = candidates
    .slice(0, 3)
    .map((c) => {
      const componentSuffix = c.componentName ? `, ${c.componentName}` : "";
      return `'${c.friendlyName}' (kuikaId ${c.kuikaId}${componentSuffix})`;
    })
    .join(", ");

  return {
    error:
      `Parameter '${parameterName}' bound to ${sourceLabel} — names share no tokens. ` +
      `Better candidate(s) on the same screen: ${suggestionText}. ` +
      `Re-bind with the matching source kuikaId, or pick another binding type ` +
      `(default for platform values, current.X for row-action context).`
  };
}

// Exported for tests.
export const __test__ = { tokenize, hasTokenOverlap };
